import React, { useState, useEffect, useLayoutEffect } from "react";
// Import external function for handling sorting data by state
import SortApplicationsByState from "../../../../data/SortApplicationsByState";
// Animation Imports
import { Fade } from "react-reveal";
// Select
import Select from 'react-select';

// Display values
const nothingShowing = 0;
const allContactsShowing = 1;
const stateShowing = 2;

// US State Values
const alabama = 0;
const alaska = 1;
const arizona = 2;
const arkansas = 3;
const california = 4;
const colorado = 5;
const connecticut = 6;
const delaware = 7;
const florida = 8;
const georgia = 9;
const hawaii = 10;
const idaho = 11;
const illinois = 12;
const indiana = 13;
const iowa = 14;
const kansas = 15;
const kentucky = 16;
const louisiana = 17;
const maine = 18;
const maryland = 19;
const massachusetts = 20;
const michigan = 21;
const minnesota = 22;
const mississippi = 23;
const missouri = 24;
const montana = 25;
const nebraska = 26;
const nevada = 27;
const newHampshire = 28;
const newJersey = 29;
const newMexico = 30;
const newYork = 31;
const northCarolina = 32;
const northDakota = 33;
const ohio = 34;
const oklahoma = 35;
const oregon = 36;
const pennsylvania = 37;
const rhodeIsland = 38;
const southCarolina = 39;
const southDakota = 40;
const tennessee = 41;
const texas = 42;
const utah = 43;
const vermont = 44;
const virginia = 45;
const washington = 46;
const westVirginia = 47;
const wisconsin = 48;
const wyoming = 49;

const defaultStateOptions = [
    { value: alabama, label: "AL" },
    { value: alaska, label: "AK" },
    { value: arizona, label: "AZ" },
    { value: arkansas, label: "AR" },
    { value: california, label: "CA" },
    { value: colorado, label: "CO" },
    { value: connecticut, label: "CT" },
    { value: delaware, label: "DE" },
    { value: florida, label: "FL" },
    { value: georgia, label: "GA" },
    { value: hawaii, label: "HI" },
    { value: idaho, label: "ID" },
    { value: illinois, label: "IL" },
    { value: indiana, label: "IN" },
    { value: iowa, label: "IA" },
    { value: kansas, label: "KS" },
    { value: kentucky, label: "KY" },
    { value: louisiana, label: "LA" },
    { value: maine, label: "ME" },
    { value: maryland, label: "MD" },
    { value: massachusetts, label: "MA" },
    { value: michigan, label: "MI" },
    { value: minnesota, label: "MN" },
    { value: mississippi, label: "MS" },
    { value: missouri, label: "MO" },
    { value: montana, label: "MT" },
    { value: nebraska, label: "NE" },
    { value: nevada, label: "NV" },
    { value: newHampshire, label: "NH" },
    { value: newJersey, label: "NJ" },
    { value: newMexico, label: "NM" },
    { value: newYork, label: "NY" },
    { value: northCarolina, label: "NC" },
    { value: northDakota, label: "ND" },
    { value: ohio, label: "OH" },
    { value: oklahoma, label: "OK" },
    { value: oregon, label: "OR" },
    { value: pennsylvania, label: "PA" },
    { value: rhodeIsland, label: "RI" },
    { value: southCarolina, label: "SC" },
    { value: southDakota, label: "SD" },
    { value: tennessee, label: "TN" },
    { value: texas, label: "TX" },
    { value: utah, label: "UT" },
    { value: vermont, label: "VT" },
    { value: virginia, label: "VA" },
    { value: washington, label: "WA" },
    { value: westVirginia, label: "WV" },
    { value: wisconsin, label: "WI" },
    { value: wyoming, label: "WY" },
];

function useWindowSize() {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
        function updateSize() {
            setSize([window.innerWidth, window.innerHeight]);
        }
        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
}

// Takes 'amount' (of applicants with a disorder) & 'contacts' (contact array) as input
// Processes to show to front end in various different ways 
export default function StatisticCategoryViewer(props) {
    // Width / Height
    const [width, height] = useWindowSize();
    // Props
    const amount = props.amount;
    const contacts = props.contacts;
    const categoryHeader = props.categoryHeader;
    // React States
    const [displaying, setDisplaying] = useState(nothingShowing);
    const [contactsByState, setContactsByState] = useState({});
    const [stateOptions, setStateOptions] = useState([]);
    const [selectedState, setSelectedState] = useState(null);
    const [patientContactsByState, setPatientContactsByState] = useState([]);
    const [caretakerContactsByState, setCaretakerContactssByState] = useState([]);
    const [familyMemberContacsByState, setFamilyMemberContactsByState] = useState([]);

    const mergeStateArrays = (patientArray, caretakerArray, familyMemberArray) => {
        const localStateArrays = {
            "Alabama": [...patientArray.Alabama, ...caretakerArray.Alabama, ...familyMemberArray.Alabama],
            "Alaska": [...patientArray.Alaska, ...caretakerArray.Alaska, ...familyMemberArray.Alaska],
            "Arizona": [...patientArray.Arizona, ...caretakerArray.Arizona, ...familyMemberArray.Arizona],
            "Arkansas": [...patientArray.Arkansas, ...caretakerArray.Arkansas, ...familyMemberArray.Arkansas],
            "California": [...patientArray.California, ...caretakerArray.California, ...familyMemberArray.California],
            "Colorado": [...patientArray.Colorado, ...caretakerArray.Colorado, ...familyMemberArray.Colorado],
            "Connecticut": [...patientArray.Connecticut, ...caretakerArray.Connecticut, ...familyMemberArray.Connecticut],
            "Delaware": [...patientArray.Delaware, ...caretakerArray.Delaware, ...familyMemberArray.Delaware],
            "Florida": [...patientArray.Florida, ...caretakerArray.Florida, ...familyMemberArray.Florida],
            "Georgia": [...patientArray.Georgia, ...caretakerArray.Georgia, ...familyMemberArray.Georgia],
            "Hawaii": [...patientArray.Hawaii, ...caretakerArray.Hawaii, ...familyMemberArray.Hawaii],
            "Idaho": [...patientArray.Idaho, ...caretakerArray.Idaho, ...familyMemberArray.Idaho],
            "Illinois": [...patientArray.Illinois, ...caretakerArray.Illinois, ...familyMemberArray.Illinois],
            "Indiana": [...patientArray.Indiana, ...caretakerArray.Indiana, ...familyMemberArray.Indiana],
            "Iowa": [...patientArray.Iowa, ...caretakerArray.Iowa, ...familyMemberArray.Iowa],
            "Kansas": [...patientArray.Kansas, ...caretakerArray.Kansas, ...familyMemberArray.Kansas],
            "Kentucky": [...patientArray.Kentucky, ...caretakerArray.Kentucky, ...familyMemberArray.Kentucky],
            "Louisiana": [...patientArray.Louisiana, ...caretakerArray.Louisiana, ...familyMemberArray.Louisiana],
            "Maine": [...patientArray.Maine, ...caretakerArray.Maine, ...familyMemberArray.Maine],
            "Maryland": [...patientArray.Maryland, ...caretakerArray.Maryland, ...familyMemberArray.Maryland],
            "Massachusetts": [...patientArray.Massachusetts, ...caretakerArray.Massachusetts, ...familyMemberArray.Massachusetts],
            "Michigan": [...patientArray.Michigan, ...caretakerArray.Michigan, ...familyMemberArray.Michigan],
            "Minnesota": [...patientArray.Minnesota, ...caretakerArray.Minnesota, ...familyMemberArray.Minnesota],
            "Mississippi": [...patientArray.Mississippi, ...caretakerArray.Mississippi, ...familyMemberArray.Mississippi],
            "Missouri": [...patientArray.Missouri, ...caretakerArray.Missouri, ...familyMemberArray.Missouri],
            "Montana": [...patientArray.Montana, ...caretakerArray.Montana, ...familyMemberArray.Montana],
            "Nebraska": [...patientArray.Nebraska, ...caretakerArray.Nebraska, ...familyMemberArray.Nebraska],
            "Nevada": [...patientArray.Nevada, ...caretakerArray.Nevada, ...familyMemberArray.Nevada],
            "NewHampshire": [...patientArray.NewHampshire, ...caretakerArray.NewHampshire, ...familyMemberArray.NewHampshire],
            "NewJersey": [...patientArray.NewJersey, ...caretakerArray.NewJersey, ...familyMemberArray.NewJersey],
            "NewMexico": [...patientArray.NewMexico, ...caretakerArray.NewMexico, ...familyMemberArray.NewMexico],
            "NewYork": [...patientArray.NewYork, ...caretakerArray.NewYork, ...familyMemberArray.NewYork],
            "NorthCarolina": [...patientArray.NorthCarolina, ...caretakerArray.NorthCarolina, ...familyMemberArray.NorthCarolina],
            "NorthDakota": [...patientArray.NorthDakota, ...caretakerArray.NorthDakota, ...familyMemberArray.NorthDakota],
            "Ohio": [...patientArray.Ohio, ...caretakerArray.Ohio, ...familyMemberArray.Ohio],
            "Oklahoma": [...patientArray.Oklahoma, ...caretakerArray.Oklahoma, ...familyMemberArray.Oklahoma],
            "Oregon": [...patientArray.Oregon, ...caretakerArray.Oregon, ...familyMemberArray.Oregon],
            "Pennsylvania": [...patientArray.Pennsylvania, ...caretakerArray.Pennsylvania, ...familyMemberArray.Pennsylvania],
            "RhodeIsland": [...patientArray.RhodeIsland, ...caretakerArray.RhodeIsland, ...familyMemberArray.RhodeIsland],
            "SouthCarolina": [...patientArray.SouthCarolina, ...caretakerArray.SouthCarolina, ...familyMemberArray.SouthCarolina],
            "SouthDakota": [...patientArray.SouthDakota, ...caretakerArray.SouthDakota, ...familyMemberArray.SouthDakota],
            "Tennessee": [...patientArray.Tennessee, ...caretakerArray.Tennessee, ...familyMemberArray.Tennessee],
            "Texas": [...patientArray.Texas, ...caretakerArray.Texas, ...familyMemberArray.Texas],
            "Utah": [...patientArray.Utah, ...caretakerArray.Utah, ...familyMemberArray.Utah],
            "Vermont": [...patientArray.Vermont, ...caretakerArray.Vermont, ...familyMemberArray.Vermont],
            "Virginia": [...patientArray.Virginia, ...caretakerArray.Virginia, ...familyMemberArray.Virginia],
            "Washington": [...patientArray.Washington, ...caretakerArray.Washington, ...familyMemberArray.Washington],
            "WestVirginia": [...patientArray.WestVirginia, ...caretakerArray.WestVirginia, ...familyMemberArray.WestVirginia],
            "Wisconsin": [...patientArray.Wisconsin, ...caretakerArray.Wisconsin, ...familyMemberArray.Wisconsin],
            "Wyoming": [...patientArray.Wyoming, ...caretakerArray.Wyoming, ...familyMemberArray.Wyoming]
        };
        return localStateArrays;
    }

    useEffect(() => {
        // Calculate State Values on load
        const localPatientContactsByState = SortApplicationsByState(props.contacts.patients);
        const localCaretakerContactsByState = SortApplicationsByState(props.contacts.caretakers);
        const localFamilyMemberContactsByState = SortApplicationsByState(props.contacts.familyMembers);

        const localContactsByState = mergeStateArrays(localPatientContactsByState, localCaretakerContactsByState, localFamilyMemberContactsByState);
        var localStateOptions = [];
        // Iterate through all states and update values accordingly
        if (localContactsByState.Alabama.length > 0) {
            localStateOptions.push({ value: alabama, label: "AL" });
        }
        if (localContactsByState.Alaska.length > 0) {
            localStateOptions.push({ value: alaska, label: "AK" });
        }
        if (localContactsByState.Arizona.length > 0) {
            localStateOptions.push({ value: arizona, label: "AZ" });
        }
        if (localContactsByState.Arkansas.length > 0) {
            localStateOptions.push({ value: arkansas, label: "AR" });
        }
        if (localContactsByState.California.length > 0) {
            localStateOptions.push({ value: california, label: "CA" });
        }
        if (localContactsByState.Colorado.length > 0) {
            localStateOptions.push({ value: colorado, label: "CO" });
        }
        if (localContactsByState.Connecticut.length > 0) {
            localStateOptions.push({ value: connecticut, label: "CT" });
        }
        if (localContactsByState.Delaware.length > 0) {
            localStateOptions.push({ value: delaware, label: "DE" });
        }
        if (localContactsByState.Florida.length > 0) {
            localStateOptions.push({ value: florida, label: "FL" });
        }
        if (localContactsByState.Georgia.length > 0) {
            localStateOptions.push({ value: georgia, label: "GA" });
        }
        if (localContactsByState.Hawaii.length > 0) {
            localStateOptions.push({ value: hawaii, label: "HI" });
        }
        if (localContactsByState.Idaho.length > 0) {
            localStateOptions.push({ value: idaho, label: "ID" });
        }
        if (localContactsByState.Illinois.length > 0) {
            localStateOptions.push({ value: illinois, label: "IL" });
        }
        if (localContactsByState.Indiana.length > 0) {
            localStateOptions.push({ value: indiana, label: "IN" });
        }
        if (localContactsByState.Iowa.length > 0) {
            localStateOptions.push({ value: iowa, label: "IA" });
        }
        if (localContactsByState.Kansas.length > 0) {
            localStateOptions.push({ value: kansas, label: "KS" });
        }
        if (localContactsByState.Kentucky.length > 0) {
            localStateOptions.push({ value: kentucky, label: "KY" });
        }
        if (localContactsByState.Louisiana.length > 0) {
            localStateOptions.push({ value: louisiana, label: "LA" });
        }
        if (localContactsByState.Maine.length > 0) {
            localStateOptions.push({ value: maine, label: "ME" });
        }
        if (localContactsByState.Maryland.length > 0) {
            localStateOptions.push({ value: maryland, label: "MD" });
        }
        if (localContactsByState.Massachusetts.length > 0) {
            localStateOptions.push({ value: massachusetts, label: "MA" });
        }
        if (localContactsByState.Michigan.length > 0) {
            localStateOptions.push({ value: michigan, label: "MI" });
        }
        if (localContactsByState.Minnesota.length > 0) {
            localStateOptions.push({ value: minnesota, label: "MN" });
        }
        if (localContactsByState.Mississippi.length > 0) {
            localStateOptions.push({ value: mississippi, label: "MS" });
        }
        if (localContactsByState.Missouri.length > 0) {
            localStateOptions.push({ value: missouri, label: "MO" });
        }
        if (localContactsByState.Montana.length > 0) {
            localStateOptions.push({ value: montana, label: "MT" });
        }
        if (localContactsByState.Nebraska.length > 0) {
            localStateOptions.push({ value: nebraska, label: "NE" });
        }
        if (localContactsByState.Nevada.length > 0) {
            localStateOptions.push({ value: nevada, label: "NV" });
        }
        if (localContactsByState.NewHampshire.length > 0) {
            localStateOptions.push({ value: newHampshire, label: "NH" });
        }
        if (localContactsByState.NewJersey.length > 0) {
            localStateOptions.push({ value: newJersey, label: "NJ" });
        }
        if (localContactsByState.NewMexico.length > 0) {
            localStateOptions.push({ value: newMexico, label: "NM" });
        }
        if (localContactsByState.NewYork.length > 0) {
            localStateOptions.push({ value: newYork, label: "NY" });
        }
        if (localContactsByState.NorthCarolina.length > 0) {
            localStateOptions.push({ value: northCarolina, label: "NC" });
        }
        if (localContactsByState.NorthDakota.length > 0) {
            localStateOptions.push({ value: northDakota, label: "ND" });
        }
        if (localContactsByState.Ohio.length > 0) {
            localStateOptions.push({ value: ohio, label: "OH" });
        }
        if (localContactsByState.Oklahoma.length > 0) {
            localStateOptions.push({ value: oklahoma, label: "OK" });
        }
        if (localContactsByState.Oregon.length > 0) {
            localStateOptions.push({ value: oregon, label: "OR" });
        }
        if (localContactsByState.Pennsylvania.length > 0) {
            localStateOptions.push({ value: pennsylvania, label: "PA" });
        }
        if (localContactsByState.RhodeIsland.length > 0) {
            localStateOptions.push({ value: rhodeIsland, label: "RI" });
        }
        if (localContactsByState.SouthCarolina.length > 0) {
            localStateOptions.push({ value: southCarolina, label: "SC" });
        }
        if (localContactsByState.SouthDakota.length > 0) {
            localStateOptions.push({ value: southDakota, label: "SD" });
        }
        if (localContactsByState.Tennessee.length > 0) {
            localStateOptions.push({ value: tennessee, label: "TN" });
        }
        if (localContactsByState.Texas.length > 0) {
            localStateOptions.push({ value: texas, label: "TX" });
        }
        if (localContactsByState.Utah.length > 0) {
            localStateOptions.push({ value: utah, label: "UT" });
        }
        if (localContactsByState.Vermont.length > 0) {
            localStateOptions.push({ value: vermont, label: "VT" });
        }
        if (localContactsByState.Virginia.length > 0) {
            localStateOptions.push({ value: virginia, label: "VA" });
        }
        if (localContactsByState.Washington.length > 0) {
            localStateOptions.push({ value: washington, label: "WA" });
        }
        if (localContactsByState.WestVirginia.length > 0) {
            localStateOptions.push({ value: westVirginia, label: "WV" });
        }
        if (localContactsByState.Wisconsin.length > 0) {
            localStateOptions.push({ value: wisconsin, label: "WI" });
        }
        if (localContactsByState.Wyoming.length > 0) {
            localStateOptions.push({ value: wyoming, label: "WY" });
        }

        // Update global values
        setContactsByState(localContactsByState);
        setStateOptions(localStateOptions);
        setPatientContactsByState(localPatientContactsByState);
        setCaretakerContactssByState(localCaretakerContactsByState);
        setFamilyMemberContactsByState(localFamilyMemberContactsByState);
    }, [contacts]);

    // Select Styling
    const selectStyles = {
        option: (provided, state) => ({
            ...provided,
            fontWeight: state.isSelected ? "bold" : "normal",
            fontSize: width <= 1260 ? "1.2rem" : "1.4rem",
            color: "#2d3436",
            backgroundColor: "#fff",
            "&:hover": {
                backgroundColor: "#f0f0f0",
            },
        }),
        control: (provided, state) => ({
            ...provided,
            borderColor: state.isFocused ? "#2d3436" : "#fff",
            margin: "12px 0 12px",
            height: width <= 1260 ? "48px" : "55px",
        }),
    };

    const renderStateSelection = () => {
        if (selectedState !== nothingShowing) {
            switch (selectedState) {
                case alabama: {
                    return (
                        <>
                            <h1 className="header">Alabama Contacts</h1>
                            {contactsByState.Alabama.array.forEach(contact => {
                                return (
                                    <div className="bleeding-disorder-contact-container">
                                        <h2>Name: {contact.name}</h2>
                                        <h2>E-Mail: {contact.email}</h2>
                                        <h2>Phone: {contact.phone}</h2>
                                        <h2>Address: </h2>
                                    </div>
                                )
                            })}
                        </>
                    )
                }
            }
        }
    }

    const renderSelection = () => {
        switch (displaying) {

            case allContactsShowing: {

            }

            case stateShowing: {
                return (
                    <>
                        <h1 className="header">Contacts By State</h1>
                        <div className="statistic-category-viewer-state-section">
                            <label>
                                Choose  State:
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={selectedState}
                                    onChange={(option) => setSelectedState(option)} options={stateOptions} />
                            </label>

                            {renderStateSelection()}
                        </div>
                    </>
                )
            }

            default:
                break;
        }
    }

    return (
        <>
            <div className="statistic-category-viewer">
                <div className='back-button'>
                    <button className='denied-button' onClick={() => props.setInCategory(false)}><i class="fa-solid fa-left-long"></i> Go Back</button>
                </div>

                <h1 className="header">{categoryHeader}</h1>
                <div className="statistic-category-viewer-nav">
                    <button className="statistic-cateogry-viewer-nav-button all-button" onClick={() => {
                        if (displaying !== !allContactsShowing) {
                            setDisplaying(allContactsShowing);
                        } else {
                            setDisplaying(nothingShowing);
                        }
                    }}>
                        View All Contacts
                    </button>

                    <button className="statistic-cateogry-viewer-nav-button search-button" onClick={() => {
                        if (displaying !== !stateShowing) {
                            setDisplaying(stateShowing);
                        } else {
                            setDisplaying(nothingShowing);
                        }
                    }}>
                        View By State
                    </button>
                </div>
                {renderSelection()}
            </div>
        </>
    )
}